import React, { useState, useRef } from 'react';
import { Download, Eye, X, Award } from 'lucide-react';
import axios from 'axios';

const Employee360CertificateCard = ({ employeeId, profile, loading }) => {
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef(null);

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const handleDownloadPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    // Create an isolated container specifically for the Certificate PDF.
    // This prevents modal, flex, overflow, and preview styles from
    // affecting the downloaded PDF.
    const pdfContainer = document.createElement('div');

    pdfContainer.style.position = 'absolute';
    pdfContainer.style.left = '-99999px';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '794px';
    pdfContainer.style.margin = '0';
    pdfContainer.style.padding = '0';
    pdfContainer.style.background = '#ffffff';
    pdfContainer.style.boxSizing = 'border-box';

    const pdfContent = printContent.cloneNode(true);

    // Reset layout-related styles for PDF rendering.
    pdfContent.style.position = 'static';
    pdfContent.style.margin = '0';
    pdfContent.style.paddingTop = '0';
    pdfContent.style.paddingBottom = '0';
    pdfContent.style.paddingLeft = '0';
    pdfContent.style.paddingRight = '0';
    pdfContent.style.width = '100%';
    pdfContent.style.maxWidth = 'none';
    pdfContent.style.minHeight = '0';
    pdfContent.style.height = 'auto';
    pdfContent.style.background = '#ffffff';

    pdfContainer.appendChild(pdfContent);
    document.body.appendChild(pdfContainer);

    // Allow the browser to render the cloned content before capture.
    await new Promise(resolve => requestAnimationFrame(resolve));

    const opt = {
      // Certificate originally used 15mm.
      // Keep a controlled top margin while reducing unnecessary space.
      margin: [5, 15, 15, 15],

      filename: `Certificate_${employeeId}.pdf`,

      image: {
        type: 'jpeg',
        quality: 0.98
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0
      },

      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      },

      pagebreak: {
        mode: ['css', 'legacy']
      }
    };

    try {
      await window
        .html2pdf()
        .set(opt)
        .from(pdfContent)
        .save();
    } finally {
      // Remove the temporary PDF-only DOM.
      document.body.removeChild(pdfContainer);
    }
  };

  const [earnedLeave, setEarnedLeave] = useState(0);
  const [hpl, setHpl] = useState(0);
  const [fetchingBalances, setFetchingBalances] = useState(true);

  React.useEffect(() => {
    const fetchLeaveSheetBalances = async () => {
      setFetchingBalances(true);
      try {
        const year = new Date().getFullYear();
        const res = await axios.get(`/v1/leave-sheet/employee/${employeeId}/earned-leave?year=${year}`);

        let elVal = 0;
        let hplVal = 0;

        if (res.data && res.data.earned_leave && res.data.earned_leave.rows) {
          const rows = res.data.earned_leave.rows;

          // EL value from Column 9 (balance_on_return) of the last row containing it
          let lastElRow = [...rows].reverse().find(r => r.balance_on_return !== null && r.balance_on_return !== undefined);
          if (lastElRow) {
            elVal = lastElRow.balance_on_return;
          }

          // HPL value from Column 31 (balance_of_return) of the last row containing it
          let lastHplRow = [...rows].reverse().find(r => r.balance_of_return !== null && r.balance_of_return !== undefined);
          if (lastHplRow) {
            hplVal = lastHplRow.balance_of_return;
          }
        }

        // Earned leave is capped at maximum 300 for Certificate report
        const cappedElVal = Math.min(Number(elVal || 0), 300);

        setEarnedLeave(cappedElVal);
        setHpl(hplVal);
      } catch (err) {
        console.error("Failed to load dynamic leave balances from leave sheet report", err);
      } finally {
        setFetchingBalances(false);
      }
    };
    fetchLeaveSheetBalances();
  }, [employeeId]);

  if (loading) return null;

  const personal = profile?.personal || {};
  const employment = profile?.employment || {};

  const name = personal.full_name || `${personal.first_name || ''} ${personal.last_name || ''}`.trim() || 'N/A';
  const designation = employment.designation || 'N/A';

  let retirementDate = null;
  if (employment.date_of_retirement) {
    retirementDate = new Date(employment.date_of_retirement);
  } else if (personal.date_of_birth) {
    retirementDate = new Date(personal.date_of_birth);
    retirementDate.setFullYear(retirementDate.getFullYear() + 60);
  }

  const formatDateDash = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  };

  const formatDateDot = (d) => formatDateDash(d);

  const todayDate = new Date();

  // Simple number to words converter for hundreds (since Earned Leave is max 300 usually)
  const numberToWords = (num) => {
    if (!num || isNaN(num)) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? " " + ones[num % 10] : "");
    if (num < 1000) {
      return ones[Math.floor(num / 100)] + " Hundred" + (num % 100 !== 0 ? " and " + numberToWords(num % 100) : "");
    }
    return num;
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <Award size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Certificate</h3>
            <p className="text-sm text-gray-500">View and download the leave account verification certificate.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1000px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Certificate Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-white rounded-lg border border-gray-200 p-12 shadow-sm overflow-x-auto">
                <div ref={printRef} className="max-w-4xl mx-auto w-full text-black bg-white pt-8 pb-16">

                  <div className="text-center mb-8">
                    <div className="font-bold text-[18px] mb-1 tracking-wide underline uppercase">CERTIFICATE</div>
                  </div>

                  <div className="px-12 text-[16px] font-serif leading-relaxed text-justify">
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;It is certified that the entries in leave account in respect of
                    <span className="font-bold uppercase"> {name}, {designation} </span>
                    of Pune Cantonment Board have been verified with the service Book and balance of full pay leave
                    (Earned leave {earnedLeave} Days and HPL - {hpl} Days) to the credit of the individual who retired on {formatDateDash(retirementDate)} as per following :-
                  </div>

                  <div className="px-12 mt-12 space-y-4 text-[16px] font-serif">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-2"></div>
                      <div className="col-span-4 uppercase">Earned Leave</div>
                      <div className="col-span-6">{earnedLeave} Days ( {numberToWords(earnedLeave)} Days )</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-2"></div>
                      <div className="col-span-4 uppercase">HPL</div>
                      <div className="col-span-6">{hpl} Days</div>
                    </div>
                  </div>

                  {/* Signatures */}
                  <div className="mt-32 pr-12 flex justify-end">
                    <div className="text-center">
                      <div className="uppercase">CHIEF ACCOUNTANT</div>
                      <div className="uppercase">DATE : {formatDateDot(todayDate)}</div>
                    </div>
                  </div>

                  <div className="mt-24 text-center">
                    <div className="font-bold uppercase">CHIEF EXECUTIVE OFFICER</div>
                    <div className="font-bold uppercase">PUNE CANTONMENT BOARD</div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Employee360CertificateCard;
